// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@sc/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@sc/App/Components/Product/ProductGridTemplate";
import xUseWidgetBadges from "@sc/App/Hooks/xUseWidgetBadges";
import { mergeWithCss } from "@sc/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showTextBadges?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    maximumNumberOfTextBadges?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    showOriginPrice?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isAuthenticated: state.context.session.isAuthenticated,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
    textBadgesStyles?: ProductTextBadgesStyles;
    priceBadgesContainer?: InjectableCss;
    loginActionButton?: ButtonProps;
    bottomSectionContainer?: InjectableCss;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showTextBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    maximumNumberOfTextBadges = 4,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    isAuthenticated,
    containerClassName,
    isPdp,
    onChangeCompare,
    showOriginPrice = true,
    maximumNumberOfImageBadges = 4,
    showImageBadges,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const history = useHistory();

    const priceAvailabilityLinkClickHandler = () => {
        history.push(`${signInUrl || "/MyAccount/SignIn"}`);
    };

    const { imageBadges, textBadges } = xUseWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            <ProductGridContainer.PriceAndActions>
                <StyledWrapper {...styles.priceBadgesContainer}></StyledWrapper>
                <StyledWrapper {...styles.bottomSectionContainer}>
                    <ProductGridContainer.Badges>
                        {showTextBadges && !!textBadges?.length && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges}
                                extendedStyles={styles.textBadgesStyles}
                            />
                        )}
                        {showImageBadges && !!imageBadges?.length && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges}
                            />
                        )}
                    </ProductGridContainer.Badges>
                    {showPrice && (
                        <ProductGridContainer.Price>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showPack={false}
                                extendedStyles={styles.productPriceStyles}
                            />
                        </ProductGridContainer.Price>
                    )}
                    {canSeePrices && showAvailability && (
                        <ProductGridContainer.Availability>
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityStyles}
                                isProductDetailsPage={isPdp}
                            />
                        </ProductGridContainer.Availability>
                    )}
                    {canSeePrices && showAddToCartActions && (
                        <ProductGridContainer.AddToCartActions>
                            <ProductActions
                                {...productActionsProps}
                                onAddToCartClick={onAddToCartClick}
                                extendedStyles={styles.productActionsStyles}
                            />
                        </ProductGridContainer.AddToCartActions>
                    )}
                    {canSeePrices && (showAddToList || showCompare) && (
                        <ProductGridContainer.AddToList>
                            <ProductListProductImage
                                showImage={false}
                                showCompare={showCompare}
                                extendedStyles={styles.productListProductImageStyles}
                            />
                            {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                        </ProductGridContainer.AddToList>
                    )}
                </StyledWrapper>
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
